import React, { useEffect, useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { add, differenceInHours, format, parseISO } from "date-fns"
import { Calendar, Clock, Download, Loader2, Upload, X } from "lucide-react"
import { Check, ChevronsUpDown } from "lucide-react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
// added import
import {
  ALL_SHERIFF_AREAS,
  ALL_SHERIFF_TYPES,
  INVALID_UK_TIME_ERROR,
  MAX_SHIFT_DURATION_HOURS,
  SHIFT_START_IN_PAST_ERROR,
  type SheriffArea,
  blockInstants,
  eachDateInRange,
  isShiftStartInPast,
  ukToday,
  ukWallClockToInstant,
} from "@/constants/sheriffOptions"
import { useClearUnavailableLocation } from "@/hooks/useClearUnavailableLocation"
import { useDepots } from "@/hooks/useDepots"
import { useSubTeams } from "@/hooks/useSubTeams"
import { cn } from "@/lib/utils"
import { type CreateShiftBody, shiftsService } from "@/services/shiftsService"
import { Person } from "@/types/person"
import { selectableDepots } from "@/utils/depotValidity"

import {
  employerNotAllowedForAreaError,
  isEmployerAllowedForArea,
} from "../../../../shared/shift-employer-rules.ts"

// Note: ROLES are now loaded dynamically via useSubTeams hook

const TYPE_OPTIONS = [...ALL_SHERIFF_TYPES]

const SHIFT_AREAS: SheriffArea[] = [...ALL_SHERIFF_AREAS]

const MAX_CSV_FILE_SIZE_BYTES = 10 * 1024 * 1024

const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
const CSV_TEMPLATE_HEADERS = [
  "startDate",
  "endDate",
  "startTime",
  "endTime",
  "area",
  "type",
  "employer",
  "location",
  "notes",
  "person",
] as const
const CSV_FORMAT_MISMATCH_ERROR = "CSV format mismatch. Check the template and try again."
const NORMALIZED_CSV_TEMPLATE_HEADERS = CSV_TEMPLATE_HEADERS.map((header) => header.toLowerCase())

type AssignmentMode = "blocks" | "persons"
type ShiftPatternOption = "none" | "4on4off" | "5on2off" | "custom"

const parseCsvTable = (csvText: string): string[][] => {
  const rows: string[][] = []
  let row: string[] = []
  let field = ""
  let inQuotes = false

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i]

    if (char === '"') {
      const next = csvText[i + 1]
      if (inQuotes && next === '"') {
        field += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (char === "," && !inQuotes) {
      row.push(field)
      field = ""
      continue
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && csvText[i + 1] === "\n") i++
      row.push(field)
      rows.push(row)
      row = []
      field = ""
      continue
    }

    field += char
  }

  row.push(field)
  rows.push(row)

  if (inQuotes) {
    throw new Error(CSV_FORMAT_MISMATCH_ERROR)
  }

  const headerRow = rows[0] || []
  const expectedColumnCount = headerRow.length

  for (let i = 1; i < rows.length; i++) {
    const currentRow = rows[i] || []
    if (currentRow.every((value) => value.trim() === "")) continue
    if (currentRow.length !== expectedColumnCount) {
      throw new Error(CSV_FORMAT_MISMATCH_ERROR)
    }
  }

  return rows
}

const sanitizeCsvHeaders = (headers: string[]): string[] =>
  headers.map((header, index) => {
    const trimmed = header.trim()
    return index === 0 ? trimmed.replace(/^\uFEFF/, "") : trimmed
  })

const formatCsvHeaderMismatchError = (receivedHeaders: string[]): string => {
  const received = receivedHeaders.length > 0 ? receivedHeaders.join(", ") : "(none)"

  return [
    "CSV headers must match the latest template columns. Column order can vary.",
    "",
    "Expected columns:",
    CSV_TEMPLATE_HEADERS.join(", "),
    "",
    "Received columns:",
    received,
  ].join("\n")
}

const getCsvHeaderValidationError = (headers: string[]): string | null => {
  const sanitizedHeaders = sanitizeCsvHeaders(headers)
  const normalizedHeaders = sanitizedHeaders.map((header) => header.toLowerCase())
  if (normalizedHeaders.length !== NORMALIZED_CSV_TEMPLATE_HEADERS.length) {
    return formatCsvHeaderMismatchError(sanitizedHeaders)
  }

  for (const expectedHeader of NORMALIZED_CSV_TEMPLATE_HEADERS) {
    if (!normalizedHeaders.includes(expectedHeader)) {
      return formatCsvHeaderMismatchError(sanitizedHeaders)
    }
  }

  return null
}

interface SimpleFormData {
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  shiftType: SheriffArea
  location: string
  role: string
  type: string
  numberOfBlocks: number
  notes: string
  selectedPersonIds: string[]
}

interface BulkFormData {
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  shiftType: SheriffArea
  location: string
  role: string
  type: string
  notes: string
  daysOfWeek: string[]
  shiftPattern: ShiftPatternOption
  customPattern: {
    workDays: number
    offDays: number
  }
  selectedPersonIds: string[]
}

interface GeneratedBlock {
  date: string
  startTime: string
  endTime: string
  isOvernight: boolean
  shiftType: SheriffArea
  location: string
  role: string
  notes: string
}

interface SimpleCreateBlocksModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onBlockCreated?: (block: unknown) => void
  defaultLocation?: string
  defaultShiftType?: string
  defaultRole?: string
  defaultType?: string
  // People data passed from parent
  people?: Person[]
  peopleLoading?: boolean
}

const SimpleCreateBlocksModal: React.FC<SimpleCreateBlocksModalProps> = ({
  open,
  onOpenChange,
  onBlockCreated,
  defaultLocation,
  defaultShiftType,
  defaultRole,
  defaultType,
  people = [],
  peopleLoading: _peopleLoading = false,
}) => {
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState("create")

  const queryClient = useQueryClient()
  const { data: depots = [] } = useDepots()
  const { subTeamOptionsWithIds } = useSubTeams()
  const defaultArea = React.useMemo(() => {
    const candidate = (defaultShiftType || "").trim()
    return (SHIFT_AREAS as readonly string[]).includes(candidate)
      ? (candidate as SheriffArea)
      : SHIFT_AREAS[0]
  }, [defaultShiftType])

  // Get roles from sub-teams
  const ALL_ROLES = React.useMemo(() => {
    return subTeamOptionsWithIds.map((opt) => opt.value)
  }, [subTeamOptionsWithIds])

  const TEAM_ROLES = React.useMemo(() => {
    return subTeamOptionsWithIds
      .filter((opt) => opt.type === "DRIVER_MANAGER_TEAM" || opt.type === "DSP")
      .map((opt) => opt.value)
  }, [subTeamOptionsWithIds])

  const rolesForArea = React.useCallback(
    (area: SheriffArea) =>
      ALL_ROLES.filter((role) => isEmployerAllowedForArea(area, role, TEAM_ROLES)),
    [ALL_ROLES, TEAM_ROLES],
  )

  // Simple form data with optional person IDs
  const [formData, setFormData] = useState<SimpleFormData>({
    startDate: ukToday(),
    endDate: ukToday(),
    startTime: "09:00",
    endTime: "17:00",
    shiftType: defaultArea,
    location: defaultLocation || "",
    role: defaultRole ?? "",
    type: defaultType ?? "",
    numberOfBlocks: 1,
    notes: "",
    selectedPersonIds: [] as string[],
  })

  const [bulkFormData, setBulkFormData] = useState<BulkFormData>({
    startDate: ukToday(),
    endDate: ukToday(),
    startTime: "09:00",
    endTime: "17:00",
    shiftType: defaultArea,
    location: "",
    role: defaultRole ?? "",
    type: defaultType ?? "",
    notes: "",
    daysOfWeek: [] as string[],
    shiftPattern: "none",
    customPattern: {
      workDays: 4,
      offDays: 4,
    },
    selectedPersonIds: [] as string[],
  })

  // Only depots open for the dates being scheduled; keep the one the modal was opened for
  const singleDepots = useMemo(
    () =>
      selectableDepots(
        depots,
        formData.startDate,
        formData.endDate,
        (depot) => depot.name === defaultLocation,
      ),
    [depots, formData.startDate, formData.endDate, defaultLocation],
  )
  const bulkDepots = useMemo(
    () =>
      selectableDepots(
        depots,
        bulkFormData.startDate,
        bulkFormData.endDate,
        (depot) => depot.name === defaultLocation,
      ),
    [depots, bulkFormData.startDate, bulkFormData.endDate, defaultLocation],
  )

  // Drop a chosen location once a date change makes it unavailable
  useClearUnavailableLocation(formData.location, singleDepots, depots.length > 0, () =>
    setFormData((prev) => ({ ...prev, location: "" })),
  )
  useClearUnavailableLocation(bulkFormData.location, bulkDepots, depots.length > 0, () =>
    setBulkFormData((prev) => ({ ...prev, location: "" })),
  )

  const [personSelectOpen, setPersonSelectOpen] = useState(false)
  const [bulkPersonSelectOpen, setBulkPersonSelectOpen] = useState(false)
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [bulkErrors, setBulkErrors] = useState<string[]>([])
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>("blocks")
  const [bulkAssignmentMode, setBulkAssignmentMode] = useState<AssignmentMode>("blocks")

  const validateCreateForm = () => {
    const errors: string[] = []

    if (!formData.location) {
      errors.push("Location is required.")
    }

    if (!formData.shiftType) {
      errors.push("Area is required.")
    }

    if (!formData.role) {
      errors.push("Role is required.")
    } else if (!isEmployerAllowedForArea(formData.shiftType, formData.role, TEAM_ROLES)) {
      errors.push(employerNotAllowedForAreaError(formData.role))
    }

    if (!formData.type) {
      errors.push("Type is required.")
    }

    const startDateTime = ukWallClockToInstant(formData.startDate, formData.startTime)
    const endDateTime = ukWallClockToInstant(formData.endDate, formData.endTime)

    if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
      errors.push(INVALID_UK_TIME_ERROR)
    } else {
      if (isShiftStartInPast(startDateTime)) {
        errors.push(SHIFT_START_IN_PAST_ERROR)
      }

      if (endDateTime <= startDateTime) {
        errors.push("End date/time must be after start date/time.")
      } else {
        const durationHours = differenceInHours(endDateTime, startDateTime)
        if (durationHours > MAX_SHIFT_DURATION_HOURS) {
          errors.push(
            `Block duration is ${durationHours} hours, which exceeds the ${MAX_SHIFT_DURATION_HOURS}-hour limit.`,
          )
        }
      }
    }

    return errors
  }

  const validateBulkForm = () => {
    const errors: string[] = []

    if (!bulkFormData.location) {
      errors.push("Location is required.")
    }

    if (!bulkFormData.shiftType) {
      errors.push("Area is required.")
    }

    if (!bulkFormData.role) {
      errors.push("Role is required.")
    } else if (!isEmployerAllowedForArea(bulkFormData.shiftType, bulkFormData.role, TEAM_ROLES)) {
      errors.push(employerNotAllowedForAreaError(bulkFormData.role))
    }

    if (!bulkFormData.type) {
      errors.push("Type is required.")
    }

    // Check the earliest block that will actually be generated, rather than assuming it
    // starts on startDate — the day-of-week and shift-pattern filters can skip that day.
    // Later blocks are all after it, so the first one is the only one that can be in the past.
    const [firstBlock] = bulkBlocksPreview
    const firstBlockStart =
      firstBlock && ukWallClockToInstant(firstBlock.date, firstBlock.startTime)
    if (firstBlockStart && !Number.isNaN(firstBlockStart.getTime())) {
      if (isShiftStartInPast(firstBlockStart)) errors.push(SHIFT_START_IN_PAST_ERROR)
    }

    // A time that doesn't exist on a clock-change day would otherwise fail halfway through creation.
    const nonexistentDates = bulkBlocksPreview
      .filter((block) => {
        const { start, end } = blockInstants(block)
        return Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())
      })
      .map((block) => block.date)
    if (nonexistentDates.length > 0) {
      errors.push(`${INVALID_UK_TIME_ERROR} Affected: ${nonexistentDates.join(", ")}.`)
    }

    if (bulkFormData.startDate > bulkFormData.endDate) {
      errors.push("Start date cannot be after end date.")
    }

    if (bulkAssignmentMode === "persons" && bulkFormData.selectedPersonIds.length === 0) {
      errors.push("Select at least one person to assign these blocks.")
    }

    // Check each selected person is compatible with the chosen area/type/employer
    if (bulkAssignmentMode === "persons" && bulkFormData.selectedPersonIds.length > 0) {
      const incompatible: string[] = []
      for (const personId of bulkFormData.selectedPersonIds) {
        const person = people.find((p) => p.id === personId)
        if (!person) continue
        const name = person.person ?? personId
        if (bulkFormData.shiftType && !person.sheriff_areas?.includes(bulkFormData.shiftType)) {
          incompatible.push(
            `${name} (area mismatch: their area is ${person.sheriff_areas?.join(", ") || "unset"})`,
          )
        } else if (bulkFormData.type && person.sheriff_type !== bulkFormData.type) {
          incompatible.push(
            `${name} (type mismatch: their type is ${person.sheriff_type || "unset"})`,
          )
        } else if (bulkFormData.role && person.sheriff_employer !== bulkFormData.role) {
          incompatible.push(
            `${name} (employer mismatch: their employer is ${person.sheriff_employer || "unset"})`,
          )
        }
      }
      if (incompatible.length > 0) {
        errors.push(
          `The following ${incompatible.length === 1 ? "person is" : "people are"} not compatible with the selected area/type/employer — reassign them or adjust the block settings:`,
        )
        incompatible.forEach((msg) => errors.push(`• ${msg}`))
      }
    }

    if (bulkFormData.startDate && bulkFormData.startTime && bulkFormData.endTime) {
      // Handles overnight shifts: an end at or before the start ends the following day
      const { start: shiftStart, end: shiftEnd } = blockInstants({
        date: bulkFormData.startDate,
        startTime: bulkFormData.startTime,
        endTime: bulkFormData.endTime,
      })
      const durationHours = differenceInHours(shiftEnd, shiftStart)
      if (durationHours > MAX_SHIFT_DURATION_HOURS) {
        errors.push(
          `Block duration is ${durationHours} hours, which exceeds the ${MAX_SHIFT_DURATION_HOURS}-hour limit.`,
        )
      }
    }

    return errors
  }

  // Filter people based on selected area, employer, and type (client-side)
  const backendArea = formData.shiftType

  const activeDrivers = React.useMemo(() => {
    return people.filter((person) => {
      if (person.person === null) return false
      if (formData.location && !person.location?.includes(formData.location)) return false
      if (backendArea && !person.sheriff_areas?.includes(backendArea)) return false
      if (formData.role && person.sheriff_employer !== formData.role) return false
      if (formData.type && person.sheriff_type !== formData.type) return false
      return true
    })
  }, [people, formData.location, backendArea, formData.role, formData.type])
  const bulkBackendArea = bulkFormData.shiftType

  const bulkActiveDrivers = React.useMemo(() => {
    return people.filter((person) => {
      if (person.person === null) return false
      if (bulkFormData.location && !person.location?.includes(bulkFormData.location)) return false
      if (bulkBackendArea && !person.sheriff_areas?.includes(bulkBackendArea)) return false
      if (bulkFormData.role && person.sheriff_employer !== bulkFormData.role) return false
      if (bulkFormData.type && person.sheriff_type !== bulkFormData.type) return false
      return true
    })
  }, [people, bulkFormData.location, bulkBackendArea, bulkFormData.role, bulkFormData.type])

  const isAssignmentReady = Boolean(
    formData.startDate &&
    formData.endDate &&
    formData.startTime &&
    formData.endTime &&
    formData.location &&
    formData.shiftType &&
    formData.role &&
    formData.type,
  )

  const isBulkAssignmentReady = Boolean(
    bulkFormData.startDate &&
    bulkFormData.endDate &&
    bulkFormData.startTime &&
    bulkFormData.endTime &&
    bulkFormData.location &&
    bulkFormData.shiftType &&
    bulkFormData.role &&
    bulkFormData.type,
  )

  // Generate bulk blocks preview based on form data
  const generateBulkBlocks = (data: BulkFormData): GeneratedBlock[] => {
    const blocks: GeneratedBlock[] = []
    let workDayCount = 0
    let offDayCount = 0
    let isWorking = true
    const isOvernight = data.endTime <= data.startTime

    for (const date of eachDateInRange(data.startDate, data.endDate)) {
      // Read the weekday of the date string itself, in UTC, so no timezone can shift it.
      const dayOfWeekShort = new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", {
        weekday: "short",
        timeZone: "UTC",
      })

      // Check if we should create a block for this date
      let shouldCreateBlock = false

      if (data.shiftPattern === "none") {
        // No shift pattern - use days of week filter
        shouldCreateBlock = data.daysOfWeek.length === 0 || data.daysOfWeek.includes(dayOfWeekShort)
      } else {
        // Apply shift pattern logic
        if (data.shiftPattern === "4on4off") {
          if (isWorking && workDayCount < 4) {
            shouldCreateBlock = true
            workDayCount++
          } else if (!isWorking && offDayCount < 4) {
            offDayCount++
          } else if (workDayCount >= 4) {
            isWorking = false
            workDayCount = 0
            offDayCount = 1
          } else if (offDayCount >= 4) {
            isWorking = true
            offDayCount = 0
            workDayCount = 1
            shouldCreateBlock = true
          }
        } else if (data.shiftPattern === "5on2off") {
          if (isWorking && workDayCount < 5) {
            shouldCreateBlock = true
            workDayCount++
          } else if (!isWorking && offDayCount < 2) {
            offDayCount++
          } else if (workDayCount >= 5) {
            isWorking = false
            workDayCount = 0
            offDayCount = 1
          } else if (offDayCount >= 2) {
            isWorking = true
            offDayCount = 0
            workDayCount = 1
            shouldCreateBlock = true
          }
        } else if (data.shiftPattern === "custom") {
          if (isWorking && workDayCount < data.customPattern.workDays) {
            shouldCreateBlock = true
            workDayCount++
          } else if (!isWorking && offDayCount < data.customPattern.offDays) {
            offDayCount++
          } else if (workDayCount >= data.customPattern.workDays) {
            isWorking = false
            workDayCount = 0
            offDayCount = 1
          } else if (offDayCount >= data.customPattern.offDays) {
            isWorking = true
            offDayCount = 0
            workDayCount = 1
            shouldCreateBlock = true
          }
        }
      }

      if (shouldCreateBlock) {
        blocks.push({
          date,
          startTime: data.startTime,
          endTime: data.endTime,
          isOvernight,
          shiftType: data.shiftType,
          location: data.location,
          role: data.role,
          notes: data.notes,
        })
      }
    }

    return blocks
  }

  const bulkBlocksPreview = useMemo(() => generateBulkBlocks(bulkFormData), [bulkFormData])

  // Update location and shift type when defaults change and modal opens
  useEffect(() => {
    if (open) {
      setFormData((prev) => ({
        ...prev,
        location: defaultLocation || prev.location,
        shiftType: defaultArea || prev.shiftType,
        role: defaultRole ?? "",
        type: defaultType ?? "",
        selectedPersonIds: [],
      }))
      setBulkFormData((prev) => ({
        ...prev,
        location: defaultLocation || prev.location,
        shiftType: defaultArea || prev.shiftType,
        role: defaultRole ?? "",
        type: defaultType ?? "",
        selectedPersonIds: [],
      }))
    }
  }, [open, defaultLocation, defaultArea, defaultRole, defaultType])

  // CSV upload state
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvPreview, setCsvPreview] = useState<Record<string, string>[]>([])
  const [csvError, setCsvError] = useState<string>("")
  const [isCsvDragActive, setIsCsvDragActive] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const validationErrors = validateCreateForm()
    if (validationErrors.length > 0) {
      setFormErrors(validationErrors)
      return
    }

    setFormErrors([])
    setIsSaving(true)

    try {
      // Find depot ID from location name
      const depot = depots.find((d) => d.name === formData.location)

      if (!formData.location) {
        throw new Error("Please select a location")
      }

      // Build ISO timestamps from date + time
      const startDateTime = ukWallClockToInstant(formData.startDate, formData.startTime)
      const endDateTime = ukWallClockToInstant(formData.endDate, formData.endTime)

      // Map shiftType (area) to backend enum value
      const backendArea = formData.shiftType
      // Use user-selected role and type instead of hardcoded mappings
      const type = formData.type
      const employer = formData.role

      // Prepare shifts for bulk creation
      const shiftsToCreate: CreateShiftBody[] = []

      if (assignmentMode === "persons" && formData.selectedPersonIds.length > 0) {
        for (const personId of formData.selectedPersonIds) {
          shiftsToCreate.push({
            startTime: startDateTime.toISOString(),
            endTime: endDateTime.toISOString(),
            area: backendArea,
            type,
            employer,
            location: formData.location,
            depotId: depot?.id || null,
            notes: formData.notes || null,
            peopleId: personId,
            notifications: true,
          })
        }
      } else {
        // Otherwise create empty blocks based on numberOfBlocks
        const numberOfBlocks = formData.numberOfBlocks || 1
        for (let i = 0; i < numberOfBlocks; i++) {
          shiftsToCreate.push({
            startTime: startDateTime.toISOString(),
            endTime: endDateTime.toISOString(),
            area: backendArea,
            type,
            employer,
            location: formData.location,
            depotId: depot?.id || null,
            notes: formData.notes || null,
            peopleId: null,
            notifications: true,
          })
        }
      }

      // Use bulk create endpoint (all-or-nothing transaction)
      const { shifts: createdShifts, count } = await shiftsService.bulkCreateShifts(shiftsToCreate)

      // Invalidate shifts query to refresh the list
      await queryClient.invalidateQueries({ queryKey: ["shifts"] })

      toast.success(
        count === 1 ? "Block created successfully!" : `${count} blocks created successfully!`,
      )
      onBlockCreated?.(createdShifts)

      // Reset form to allow creating another block
      setFormData({
        startDate: ukToday(),
        endDate: ukToday(),
        startTime: "09:00",
        endTime: "17:00",
        shiftType: defaultArea,
        location: defaultLocation || "",
        role: defaultRole ?? "",
        type: defaultType ?? "",
        numberOfBlocks: 1,
        notes: "",
        selectedPersonIds: [],
      })
      setAssignmentMode("blocks")

      // Close modal
      handleOpenChange(false)
    } catch (err) {
      console.error("Failed to create block:", err)
      const message = err instanceof Error ? err.message : "Failed to create block"
      setFormErrors([message])
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const validationErrors = validateBulkForm()
    if (validationErrors.length > 0) {
      setBulkErrors(validationErrors)
      return
    }

    setBulkErrors([])

    const blocks = [...bulkBlocksPreview]
    if (blocks.length === 0) {
      toast.error("No blocks match the selected criteria. Adjust the schedule range or filters.")
      return
    }

    setIsSaving(true)

    const backendArea = bulkFormData.shiftType
    // Use user-selected role and type instead of hardcoded mappings
    const type = bulkFormData.type
    const employer = bulkFormData.role
    const depot = depots.find((d) => d.name === bulkFormData.location)

    try {
      // Prepare all shifts for bulk creation
      const shiftsToCreate: CreateShiftBody[] = []

      if (bulkAssignmentMode === "persons" && bulkFormData.selectedPersonIds.length > 0) {
        // Create shifts for each person and each block
        for (const personId of bulkFormData.selectedPersonIds) {
          for (const block of blocks) {
            const { start, end } = blockInstants(block)
            shiftsToCreate.push({
              startTime: start.toISOString(),
              endTime: end.toISOString(),
              area: backendArea,
              type,
              employer,
              location: block.location,
              depotId: depot?.id || null,
              notes: block.notes ? block.notes : null,
              peopleId: personId,
              notifications: true,
            })
          }
        }
      } else {
        // Create empty blocks
        for (const block of blocks) {
          const { start, end } = blockInstants(block)
          shiftsToCreate.push({
            startTime: start.toISOString(),
            endTime: end.toISOString(),
            area: backendArea,
            type,
            employer,
            location: block.location,
            depotId: depot?.id || null,
            notes: block.notes ? block.notes : null,
            peopleId: null,
            notifications: true,
          })
        }
      }

      // Use bulk create endpoint (all-or-nothing transaction)
      const { shifts: createdShifts, count } = await shiftsService.bulkCreateShifts(shiftsToCreate)

      await queryClient.invalidateQueries({ queryKey: ["shifts"] })

      onBlockCreated?.(createdShifts)
      toast.success(`${count} block${count === 1 ? "" : "s"} created successfully!`)
      handleOpenChange(false)
    } catch (err) {
      console.error("Failed to create bulk blocks:", err)
      const message =
        err instanceof Error
          ? err.message
          : "Failed to create one or more blocks. Please try again."
      setBulkErrors([message])
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCsvUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!csvFile) return

    setIsSaving(true)
    setCsvError("")

    try {
      // Read CSV file content
      const text = await csvFile.text()
      const table = parseCsvTable(text)
      const headers = sanitizeCsvHeaders(table[0] || [])
      const headerError = getCsvHeaderValidationError(headers)

      if (headerError) {
        setCsvError(headerError)
        return
      }

      // Upload to backend
      const { shifts, count, errors } = await shiftsService.csvUploadShifts(text)

      await queryClient.invalidateQueries({ queryKey: ["shifts"] })

      onBlockCreated?.(shifts)

      if (errors && errors.length > 0) {
        toast.warning(
          `${count} block${count === 1 ? "" : "s"} created successfully, but ${errors.length} row${errors.length === 1 ? "" : "s"} had errors. Check console for details.`,
        )
        console.warn("CSV upload errors:", errors)
      } else {
        toast.success(`${count} block${count === 1 ? "" : "s"} created successfully from CSV!`)
      }

      handleOpenChange(false)
    } catch (err) {
      console.error("Failed to upload CSV:", err)
      const errorMessage = err instanceof Error ? err.message : "Failed to upload CSV"
      setCsvError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const downloadCsvTemplate = () => {
    // Sample dates are relative to today: blocks that start in the past are rejected on
    // upload, so a template with hardcoded dates would eventually fail as written.
    const tomorrow = format(add(new Date(), { days: 1 }), "yyyy-MM-dd")
    const dayAfter = format(add(new Date(), { days: 2 }), "yyyy-MM-dd")

    const sampleData = [
      [
        tomorrow,
        dayAfter,
        "22:00",
        "06:00",
        "Rc1 Deliveries",
        "HivedVan",
        "HIVED",
        "Silvertown Depot",
        "Overnight block sample",
        "12345-JonSnow",
      ],
      [
        dayAfter,
        dayAfter,
        "09:00",
        "17:00",
        "Collections",
        "HivedVan",
        "HIVED",
        "Hayes Depot",
        "Morning collection",
        "",
      ],
    ]

    const csvContent = [
      CSV_TEMPLATE_HEADERS.join(","),
      ...sampleData.map((row) => row.join(",")),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "blocks_template.csv"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const handleCsvSelectedFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setCsvFile(null)
      setCsvPreview([])
      setCsvError("Please upload a CSV file.")
      return
    }

    if (file.size > MAX_CSV_FILE_SIZE_BYTES) {
      setCsvFile(null)
      setCsvPreview([])
      setCsvError("File is too large. Maximum size is 10MB.")
      return
    }

    setCsvFile(null)
    setCsvPreview([])
    setCsvError("")

    // Parse CSV file
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        const table = parseCsvTable(text)
        const headers = sanitizeCsvHeaders(table[0] || [])
        const headerError = getCsvHeaderValidationError(headers)

        if (headerError) {
          setCsvFile(null)
          setCsvPreview([])
          setCsvError(headerError)
          return
        }

        const preview = table
          .slice(1)
          .filter((values) => values.some((value) => value.trim() !== ""))
          .slice(0, 5)
          .map((values) => {
            const row: Record<string, string> = {}
            headers.forEach((header, index) => {
              row[header] = (values[index] || "").trim()
            })
            return row
          })

        setCsvFile(file)
        setCsvPreview(preview)
        setCsvError("")
      } catch {
        setCsvFile(null)
        setCsvPreview([])
        setCsvError(CSV_FORMAT_MISMATCH_ERROR)
      }
    }
    reader.onerror = () => {
      setCsvFile(null)
      setCsvPreview([])
      setCsvError("Failed to read the CSV file. Please try again.")
    }
    reader.readAsText(file)
  }

  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    handleCsvSelectedFile(file)
    e.target.value = ""
  }

  const handleCsvDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsCsvDragActive(true)
  }

  const handleCsvDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = "copy"
  }

  const handleCsvDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const relatedTarget = e.relatedTarget as Node | null
    if (relatedTarget && e.currentTarget.contains(relatedTarget)) {
      return
    }
    setIsCsvDragActive(false)
  }

  const handleCsvDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsCsvDragActive(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    handleCsvSelectedFile(file)
  }

  const resetForm = () => {
    setFormData({
      startDate: ukToday(),
      endDate: ukToday(),
      startTime: "09:00",
      endTime: "17:00",
      shiftType: defaultArea,
      location: "",
      role: defaultRole ?? "",
      type: defaultType ?? "",
      numberOfBlocks: 1,
      notes: "",
      selectedPersonIds: [],
    })
    setAssignmentMode("blocks")
    setPersonSelectOpen(false)
    setBulkPersonSelectOpen(false)
    setBulkAssignmentMode("blocks")
    setBulkFormData({
      startDate: ukToday(),
      endDate: ukToday(),
      startTime: "09:00",
      endTime: "17:00",
      shiftType: defaultArea,
      location: "",
      role: defaultRole ?? "",
      type: defaultType ?? "",
      notes: "",
      daysOfWeek: [] as string[],
      shiftPattern: "none",
      customPattern: {
        workDays: 4,
        offDays: 4,
      },
      selectedPersonIds: [],
    })
    setCsvFile(null)
    setCsvPreview([])
    setCsvError("")
    setIsCsvDragActive(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm()
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          activeTab === "csv" ? "max-w-6xl" : "max-w-2xl",
          "max-h-[90vh] overflow-y-auto",
        )}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Create Blocks</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="create">Create Block</TabsTrigger>
            <TabsTrigger value="bulk">Create Bulk Blocks</TabsTrigger>
            <TabsTrigger value="csv">CSV Upload</TabsTrigger>
          </TabsList>

          {/* Create Single Block Tab */}
          <TabsContent value="create" className="mt-4 space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {formErrors.length > 0 && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <ul className="list-disc space-y-1 pl-5">
                    {formErrors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Start Date & Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.startDate && "text-muted-foreground",
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {formData.startDate ? (
                          format(parseISO(formData.startDate), "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.startDate ? parseISO(formData.startDate) : undefined}
                        onSelect={(date) =>
                          date &&
                          setFormData((prev) => ({
                            ...prev,
                            startDate: format(date, "yyyy-MM-dd"),
                            // Keep the end date valid if it now precedes the start date
                            endDate:
                              prev.endDate < format(date, "yyyy-MM-dd")
                                ? format(date, "yyyy-MM-dd")
                                : prev.endDate,
                          }))
                        }
                        disabled={{ before: parseISO(ukToday()) }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="startTime">Start Time (UK)</Label>
                  <div className="relative">
                    <Input
                      id="startTime"
                      type="time"
                      value={formData.startTime}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          startTime: e.target.value,
                        }))
                      }
                      required
                      className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    />
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* End Date & Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.endDate && "text-muted-foreground",
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {formData.endDate ? (
                          format(parseISO(formData.endDate), "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.endDate ? parseISO(formData.endDate) : undefined}
                        onSelect={(date) =>
                          date &&
                          setFormData((prev) => ({
                            ...prev,
                            endDate: format(date, "yyyy-MM-dd"),
                          }))
                        }
                        disabled={{
                          before: formData.startDate
                            ? parseISO(formData.startDate)
                            : parseISO(ukToday()),
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endTime">End Time (UK)</Label>
                  <div className="relative">
                    <Input
                      id="endTime"
                      type="time"
                      value={formData.endTime}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          endTime: e.target.value,
                        }))
                      }
                      required
                      className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    />
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Location & Area */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Select
                    value={formData.location}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, location: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {singleDepots.map((depot) => (
                        <SelectItem key={depot.id} value={depot.name}>
                          {depot.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shiftType">Area</Label>
                  <Select
                    value={formData.shiftType}
                    onValueChange={(value: SheriffArea) =>
                      setFormData((prev) => ({ ...prev, shiftType: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent>
                      {SHIFT_AREAS.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Role & Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {rolesForArea(formData.shiftType).map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Assignment Mode */}
              <div className="space-y-4 rounded-md border border-palette-jungleGreen200 p-4 bg-palette-jungleGreen000">
                <div className="space-y-2">
                  <Label htmlFor="assignmentMode">Assignment Mode</Label>
                  <Select
                    value={assignmentMode}
                    onValueChange={(mode: "blocks" | "persons") => {
                      setAssignmentMode(mode)
                      if (mode === "blocks") {
                        setFormData((prev) => ({
                          ...prev,
                          selectedPersonIds: [],
                        }))
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          numberOfBlocks: 1,
                        }))
                      }
                    }}
                  >
                    <SelectTrigger disabled={!isAssignmentReady}>
                      <SelectValue placeholder="Select assignment mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blocks">Create empty blocks</SelectItem>
                      <SelectItem value="persons">Assign to drivers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {assignmentMode === "blocks" && (
                  <div className="space-y-2">
                    <Label htmlFor="numberOfBlocks">Number of Blocks</Label>
                    <Input
                      id="numberOfBlocks"
                      type="number"
                      min="1"
                      max="50"
                      value={formData.numberOfBlocks}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          numberOfBlocks: parseInt(e.target.value) || 1,
                        }))
                      }
                      disabled={!isAssignmentReady}
                    />
                    <p className="text-xs text-palette-jungleGreen500">
                      Create multiple empty blocks at once
                    </p>
                  </div>
                )}

                {assignmentMode === "persons" && (
                  <div className="space-y-2">
                    <Label>Assign to Persons</Label>
                    <Popover
                      open={isAssignmentReady ? personSelectOpen : false}
                      onOpenChange={(openState) => {
                        if (!isAssignmentReady) return
                        setPersonSelectOpen(openState)
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={personSelectOpen}
                          className="w-full justify-between"
                          disabled={!isAssignmentReady}
                        >
                          {formData.selectedPersonIds.length > 0
                            ? `${formData.selectedPersonIds.length} person${
                                formData.selectedPersonIds.length > 1 ? "s" : ""
                              } selected`
                            : "Select persons..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-full p-0"
                        align="start"
                        onWheelCapture={(e) => e.stopPropagation()}
                        onWheel={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}
                      >
                        <Command className="w-full">
                          <CommandInput placeholder="Search drivers..." />
                          <CommandList
                            className="max-h-64 overscroll-contain"
                            data-scroll-lock-scrollable
                            onWheelCapture={(e) => e.stopPropagation()}
                            onWheel={(e) => e.stopPropagation()}
                            onTouchMove={(e) => e.stopPropagation()}
                          >
                            <CommandEmpty>No drivers found.</CommandEmpty>
                            <CommandGroup>
                              {activeDrivers.map((driver) => (
                                <CommandItem
                                  key={driver.id}
                                  value={driver.person}
                                  onSelect={() => {
                                    setFormData((prev) => {
                                      const isSelected = prev.selectedPersonIds.includes(driver.id)
                                      return {
                                        ...prev,
                                        selectedPersonIds: isSelected
                                          ? prev.selectedPersonIds.filter((id) => id !== driver.id)
                                          : [...prev.selectedPersonIds, driver.id],
                                      }
                                    })
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.selectedPersonIds.includes(driver.id)
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  {driver.person}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {formData.selectedPersonIds.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.selectedPersonIds.map((personId) => {
                          const driver = activeDrivers.find((d) => d.id === personId)
                          return driver ? (
                            <Badge key={personId} variant="secondary" className="gap-1">
                              {driver.person}
                              <X
                                className="h-3 w-3 cursor-pointer"
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    selectedPersonIds: prev.selectedPersonIds.filter(
                                      (id) => id !== personId,
                                    ),
                                  }))
                                }}
                              />
                            </Badge>
                          ) : null
                        })}
                      </div>
                    )}
                    <p className="text-xs text-palette-jungleGreen500">
                      {!isAssignmentReady
                        ? "Select date, time, location, area, role, and type to assign drivers"
                        : `${activeDrivers.length} active driver${
                            activeDrivers.length !== 1 ? "s" : ""
                          } available`}
                    </p>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any additional notes..."
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Block
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Bulk Block Creation Tab */}
          <TabsContent value="bulk" className="mt-4 space-y-6">
            <form onSubmit={handleBulkSubmit} className="space-y-6">
              {bulkErrors.length > 0 && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <ul className="list-disc space-y-1 pl-5">
                    {bulkErrors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !bulkFormData.startDate && "text-muted-foreground",
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {bulkFormData.startDate ? (
                          format(parseISO(bulkFormData.startDate), "PPP")
                        ) : (
                          <span>Pick start date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={
                          bulkFormData.startDate ? parseISO(bulkFormData.startDate) : undefined
                        }
                        onSelect={(date) =>
                          date &&
                          setBulkFormData((prev) => ({
                            ...prev,
                            startDate: format(date, "yyyy-MM-dd"),
                            // Keep the end date valid if it now precedes the start date
                            endDate:
                              prev.endDate < format(date, "yyyy-MM-dd")
                                ? format(date, "yyyy-MM-dd")
                                : prev.endDate,
                          }))
                        }
                        disabled={{ before: parseISO(ukToday()) }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !bulkFormData.endDate && "text-muted-foreground",
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {bulkFormData.endDate ? (
                          format(parseISO(bulkFormData.endDate), "PPP")
                        ) : (
                          <span>Pick end date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={bulkFormData.endDate ? parseISO(bulkFormData.endDate) : undefined}
                        onSelect={(date) =>
                          date &&
                          setBulkFormData((prev) => ({
                            ...prev,
                            endDate: format(date, "yyyy-MM-dd"),
                          }))
                        }
                        disabled={{
                          before: bulkFormData.startDate
                            ? parseISO(bulkFormData.startDate)
                            : parseISO(ukToday()),
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="shiftPattern">Schedule Type</Label>
                  <Select
                    value={bulkFormData.shiftPattern}
                    onValueChange={(value: "none" | "4on4off" | "5on2off" | "custom") =>
                      setBulkFormData((prev) => ({
                        ...prev,
                        shiftPattern: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select schedule type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Days of Week</SelectItem>
                      <SelectItem value="4on4off">Rota Pattern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {bulkFormData.shiftPattern === "4on4off" && (
                  <div className="space-y-2">
                    <Label>Rota Pattern</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="workDays">Work Days</Label>
                        <Input
                          id="workDays"
                          type="number"
                          min="1"
                          max="14"
                          value={bulkFormData.customPattern.workDays}
                          onChange={(e) =>
                            setBulkFormData((prev) => ({
                              ...prev,
                              customPattern: {
                                ...prev.customPattern,
                                workDays: parseInt(e.target.value) || 1,
                              },
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="offDays">Off Days</Label>
                        <Input
                          id="offDays"
                          type="number"
                          min="1"
                          max="14"
                          value={bulkFormData.customPattern.offDays}
                          onChange={(e) =>
                            setBulkFormData((prev) => ({
                              ...prev,
                              customPattern: {
                                ...prev.customPattern,
                                offDays: parseInt(e.target.value) || 1,
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}

                {bulkFormData.shiftPattern === "none" && (
                  <div className="space-y-2">
                    <Label>Days of Week (Optional - Leave empty for all days)</Label>
                    <div className="grid grid-cols-7 gap-2">
                      {DAYS_OF_WEEK.map((day) => (
                        <Button
                          key={day}
                          type="button"
                          variant={bulkFormData.daysOfWeek.includes(day) ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (bulkFormData.daysOfWeek.includes(day)) {
                              setBulkFormData((prev) => ({
                                ...prev,
                                daysOfWeek: prev.daysOfWeek.filter((d) => d !== day),
                              }))
                            } else {
                              setBulkFormData((prev) => ({
                                ...prev,
                                daysOfWeek: [...prev.daysOfWeek, day],
                              }))
                            }
                          }}
                          className="text-xs px-2"
                        >
                          {day}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Time Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bulkStartTime">Start Time (UK)</Label>
                  <div className="relative">
                    <Input
                      id="bulkStartTime"
                      type="time"
                      value={bulkFormData.startTime}
                      onChange={(e) =>
                        setBulkFormData((prev) => ({
                          ...prev,
                          startTime: e.target.value,
                        }))
                      }
                      required
                      className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    />
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black pointer-events-none" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bulkEndTime">End Time (UK)</Label>
                  <div className="relative">
                    <Input
                      id="bulkEndTime"
                      type="time"
                      value={bulkFormData.endTime}
                      onChange={(e) =>
                        setBulkFormData((prev) => ({
                          ...prev,
                          endTime: e.target.value,
                        }))
                      }
                      required
                      className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    />
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Location & Area */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bulkLocation">Location</Label>
                  <Select
                    value={bulkFormData.location}
                    onValueChange={(value) =>
                      setBulkFormData((prev) => ({ ...prev, location: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {bulkDepots.map((depot) => (
                        <SelectItem key={depot.id} value={depot.name}>
                          {depot.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bulkShiftType">Area</Label>
                  <Select
                    value={bulkFormData.shiftType}
                    onValueChange={(value: SheriffArea) =>
                      setBulkFormData((prev) => ({ ...prev, shiftType: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent>
                      {SHIFT_AREAS.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Role & Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bulkRole">Role</Label>
                  <Select
                    value={bulkFormData.role}
                    onValueChange={(value) => setBulkFormData((prev) => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {rolesForArea(bulkFormData.shiftType).map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bulkType">Type</Label>
                  <Select
                    value={bulkFormData.type}
                    onValueChange={(value) => setBulkFormData((prev) => ({ ...prev, type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Bulk Assignment Mode */}
              <div className="space-y-4 rounded-md border border-palette-jungleGreen200 p-4 bg-palette-jungleGreen000">
                <div className="space-y-2">
                  <Label htmlFor="bulkAssignmentMode">Assignment Mode</Label>
                  <Select
                    value={bulkAssignmentMode}
                    onValueChange={(mode: "blocks" | "persons") => {
                      setBulkAssignmentMode(mode)
                      if (mode === "blocks") {
                        setBulkFormData((prev) => ({
                          ...prev,
                          selectedPersonIds: [],
                        }))
                        setBulkPersonSelectOpen(false)
                      } else {
                        setBulkFormData((prev) => ({
                          ...prev,
                          selectedPersonIds: prev.selectedPersonIds ?? [],
                        }))
                      }
                    }}
                  >
                    <SelectTrigger disabled={!isBulkAssignmentReady}>
                      <SelectValue placeholder="Select assignment mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blocks">Create empty blocks</SelectItem>
                      <SelectItem value="persons">Assign to drivers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {bulkAssignmentMode === "blocks" && (
                  <div className="space-y-2">
                    <p className="text-sm text-palette-jungleGreen600">
                      Empty blocks will be created without a person assignment.
                    </p>
                  </div>
                )}

                {bulkAssignmentMode === "persons" && (
                  <div className="space-y-2">
                    <Label>Assign to Persons</Label>
                    <Popover
                      open={isBulkAssignmentReady ? bulkPersonSelectOpen : false}
                      onOpenChange={(openState) => {
                        if (!isBulkAssignmentReady) return
                        setBulkPersonSelectOpen(openState)
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={bulkPersonSelectOpen}
                          className="w-full justify-between"
                          disabled={!isBulkAssignmentReady}
                        >
                          {bulkFormData.selectedPersonIds.length > 0
                            ? `${bulkFormData.selectedPersonIds.length} person${
                                bulkFormData.selectedPersonIds.length > 1 ? "s" : ""
                              } selected`
                            : "Select persons..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-full p-0"
                        align="start"
                        onWheelCapture={(e) => e.stopPropagation()}
                        onWheel={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}
                      >
                        <Command className="w-full">
                          <CommandInput placeholder="Search drivers..." />
                          <CommandList
                            className="max-h-64 overscroll-contain"
                            data-scroll-lock-scrollable
                            onWheelCapture={(e) => e.stopPropagation()}
                            onWheel={(e) => e.stopPropagation()}
                            onTouchMove={(e) => e.stopPropagation()}
                          >
                            <CommandEmpty>No drivers found.</CommandEmpty>
                            <CommandGroup>
                              {bulkActiveDrivers.map((driver) => (
                                <CommandItem
                                  key={driver.id}
                                  value={driver.person}
                                  onSelect={() => {
                                    setBulkFormData((prev) => {
                                      const isSelected = prev.selectedPersonIds.includes(driver.id)
                                      return {
                                        ...prev,
                                        selectedPersonIds: isSelected
                                          ? prev.selectedPersonIds.filter((id) => id !== driver.id)
                                          : [...prev.selectedPersonIds, driver.id],
                                      }
                                    })
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      bulkFormData.selectedPersonIds.includes(driver.id)
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  {driver.person}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {bulkFormData.selectedPersonIds.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {bulkFormData.selectedPersonIds.map((personId) => {
                          const driver = bulkActiveDrivers.find((d) => d.id === personId)
                          return driver ? (
                            <Badge key={personId} variant="secondary" className="gap-1">
                              {driver.person}
                              <X
                                className="h-3 w-3 cursor-pointer"
                                onClick={() => {
                                  setBulkFormData((prev) => ({
                                    ...prev,
                                    selectedPersonIds: prev.selectedPersonIds.filter(
                                      (id) => id !== personId,
                                    ),
                                  }))
                                }}
                              />
                            </Badge>
                          ) : null
                        })}
                      </div>
                    )}
                    <p className="text-xs text-palette-jungleGreen500">
                      {!isBulkAssignmentReady
                        ? "Select date range, times, location, area, role, and type to assign drivers"
                        : `${bulkActiveDrivers.length} active driver${
                            bulkActiveDrivers.length !== 1 ? "s" : ""
                          } available`}
                    </p>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="bulkNotes">Notes (Optional)</Label>
                <textarea
                  id="bulkNotes"
                  value={bulkFormData.notes}
                  onChange={(e) =>
                    setBulkFormData((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  placeholder="Add any additional notes for all blocks..."
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  rows={3}
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Preview:</strong> This will create approximately{" "}
                  {bulkFormData.startDate && bulkFormData.endDate
                    ? bulkAssignmentMode === "persons" && bulkFormData.selectedPersonIds.length > 0
                      ? bulkBlocksPreview.length * bulkFormData.selectedPersonIds.length
                      : bulkBlocksPreview.length
                    : 0}{" "}
                  block
                  {bulkFormData.startDate &&
                  bulkFormData.endDate &&
                  ((bulkAssignmentMode === "persons" &&
                    bulkFormData.selectedPersonIds.length > 0 &&
                    bulkBlocksPreview.length * bulkFormData.selectedPersonIds.length !== 1) ||
                    (bulkAssignmentMode !== "persons" && bulkBlocksPreview.length !== 1) ||
                    bulkBlocksPreview.length !== 1)
                    ? "s"
                    : ""}{" "}
                  {bulkAssignmentMode === "persons" && bulkFormData.selectedPersonIds.length > 0
                    ? `(${bulkBlocksPreview.length} date${
                        bulkBlocksPreview.length !== 1 ? "s" : ""
                      } × ${bulkFormData.selectedPersonIds.length} driver${
                        bulkFormData.selectedPersonIds.length !== 1 ? "s" : ""
                      })`
                    : "for the selected date range and days"}
                  .
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Bulk Blocks
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* CSV Upload Tab */}
          <TabsContent value="csv" className="mt-4 space-y-6">
            <form onSubmit={handleCsvUpload} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="csvFile">Upload CSV File</Label>
                  <div
                    className={cn(
                      "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
                      isCsvDragActive
                        ? "border-blue-500 bg-blue-50"
                        : "border-palette-jungleGreen300 hover:border-palette-jungleGreen400",
                    )}
                    onDragEnter={handleCsvDragEnter}
                    onDragOver={handleCsvDragOver}
                    onDragLeave={handleCsvDragLeave}
                    onDrop={handleCsvDrop}
                  >
                    <input
                      id="csvFile"
                      type="file"
                      accept=".csv"
                      onChange={handleCsvFileChange}
                      className="hidden"
                    />
                    <label htmlFor="csvFile" className="cursor-pointer">
                      <Upload className="mx-auto h-12 w-12 text-palette-jungleGreen400" />
                      <p className="mt-2 text-sm text-palette-jungleGreen600">
                        Click to upload or drag and drop your CSV file
                      </p>
                      <p className="text-xs text-palette-jungleGreen500 mt-1">
                        CSV files only, max 10MB
                      </p>
                      <p className="text-xs text-amber-800 mt-1 font-medium bg-amber-100 rounded px-2 py-1 inline-block">
                        Dates and times in the CSV must be UK local time (GMT or BST)
                      </p>
                    </label>
                  </div>
                  {csvFile && (
                    <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-green-800">{csvFile.name}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCsvFile(null)
                          setCsvPreview([])
                          setCsvError("")
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {csvError && (
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-sm text-red-800 whitespace-pre-wrap">{csvError}</p>
                  </div>
                )}

                {csvPreview.length > 0 && (
                  <div className="space-y-2">
                    <Label>CSV Preview (First 5 rows)</Label>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-palette-jungleGreen000">
                            <tr>
                              {Object.keys(csvPreview[0] || {}).map((header) => (
                                <th
                                  key={header}
                                  className="px-3 py-2 text-left font-medium text-palette-jungleGreen900"
                                >
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-palette-jungleGreen200">
                            {csvPreview.map((row, index) => (
                              <tr key={index} className="hover:bg-palette-jungleGreen000">
                                {Object.values(row).map((value, cellIndex) => (
                                  <td
                                    key={cellIndex}
                                    className="px-3 py-2 text-palette-jungleGreen900"
                                  >
                                    {String(value)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <p className="text-xs text-palette-jungleGreen500">
                      Preview shows first 5 rows. All matching rows will be processed.
                    </p>
                  </div>
                )}

                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-blue-900 mb-1">CSV Template</h4>
                      <p className="text-sm text-blue-800">Download the CSV template format.</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={downloadCsvTemplate}
                      className="bg-white hover:bg-blue-50"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download Template
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving || !csvFile}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Upload & Create Blocks
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

export default SimpleCreateBlocksModal
